import { useCallback, useEffect, useRef, useState } from 'react';
import { detectArrows } from '../game/detectArrows';

interface Props {
  file: File;
  /** Receives the cleaned image. The original never leaves this component. */
  onApply: (cleaned: Blob) => void;
  onCancel: () => void;
}

interface Box {
  /** Image pixel coordinates, not screen coordinates. */
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Redact a screenshot before it is uploaded.
 *
 * ---------------------------------------------------------------------------
 * THE REDACTION IS BURNED IN, AND IT HAPPENS HERE, NOT ON THE SERVER.
 *
 * The boxes are painted into the pixels and the result is re-encoded; the
 * original file is never uploaded and never touches disk. Storing an original
 * plus a list of "areas to cover" would mean the sensitive pixels still exist
 * on the server, one misconfigured route or one debug endpoint away from being
 * served. Covering them in the browser means they never leave the machine that
 * already had them.
 *
 * Re-encoding also drops every piece of metadata the original carried — EXIF,
 * timestamps, device and GPS tags. A screenshot rarely has them; a photo of a
 * screen very much does, and that is a thing people upload.
 * ---------------------------------------------------------------------------
 *
 * There is deliberately no "automatically find the sensitive parts" button.
 * Finding names and email addresses in a screenshot means OCR, and OCR on small
 * interface text misses things. A tool that reports "nothing sensitive found"
 * and is wrong is worse than no tool at all, because the person stops looking.
 * The checklist below exists to make them look.
 */
export function Redactor({ file, onApply, onCancel }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const bitmapRef = useRef<ImageBitmap | null>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [drawing, setDrawing] = useState<Box | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  /**
   * How many arrows the current boxes would destroy.
   *
   * Counted rather than warned about. "A box over an arrow removes it" is a
   * sentence people skim; "this removes 2 of 10 arrows" is a fact they have to
   * answer. Only set once Apply has been pressed, so the count is never stale.
   */
  const [arrowCost, setArrowCost] = useState<{ before: number; after: number } | null>(null);

  // Load once and keep the decoded bitmap: re-decoding on every repaint makes
  // dragging a box visibly stutter on a large screenshot.
  useEffect(() => {
    let cancelled = false;
    let bitmap: ImageBitmap | null = null;
    void createImageBitmap(file).then((loaded) => {
      if (cancelled) {
        loaded.close();
        return;
      }
      bitmap = loaded;
      bitmapRef.current = loaded;
      setSize({ w: loaded.width, h: loaded.height });
    });
    return () => {
      cancelled = true;
      bitmap?.close();
      bitmapRef.current = null;
    };
  }, [file]);

  const repaint = useCallback(() => {
    const canvas = canvasRef.current;
    const bitmap = bitmapRef.current;
    if (!canvas || !bitmap) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.drawImage(bitmap, 0, 0);
    context.fillStyle = '#000';
    for (const box of boxes) context.fillRect(box.x, box.y, box.w, box.h);
    if (drawing) {
      context.fillStyle = 'rgba(0, 0, 0, 0.55)';
      context.fillRect(drawing.x, drawing.y, drawing.w, drawing.h);
      context.strokeStyle = '#ff5c4d';
      context.lineWidth = Math.max(2, bitmap.width / 500);
      context.strokeRect(drawing.x, drawing.y, drawing.w, drawing.h);
    }
  }, [boxes, drawing]);

  useEffect(() => {
    repaint();
  }, [repaint, size]);

  /** Screen coordinates to image pixels; the canvas is displayed scaled. */
  const toImage = useCallback((event: React.PointerEvent<HTMLCanvasElement>): Box => {
    const canvas = event.currentTarget;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
      w: 0,
      h: 0,
    };
  }, []);

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      event.currentTarget.setPointerCapture(event.pointerId);
      setDrawing(toImage(event));
    },
    [toImage],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      if (!drawing) return;
      const point = toImage(event);
      setDrawing({ x: drawing.x, y: drawing.y, w: point.x - drawing.x, h: point.y - drawing.y });
    },
    [drawing, toImage],
  );

  const onPointerUp = useCallback(() => {
    if (!drawing) return;
    // Normalise so a box dragged up or left is still a positive rectangle.
    const box: Box = {
      x: Math.min(drawing.x, drawing.x + drawing.w),
      y: Math.min(drawing.y, drawing.y + drawing.h),
      w: Math.abs(drawing.w),
      h: Math.abs(drawing.h),
    };
    setDrawing(null);
    // A click rather than a drag is not a redaction; ignore it instead of
    // leaving an invisible zero-size box in the list.
    if (box.w < 4 || box.h < 4) return;
    // A new box changes the arithmetic, so any previous count is stale.
    setArrowCost(null);
    setBoxes((previous) => [...previous, box]);
  }, [drawing]);

  const apply = useCallback(
    (acknowledged = false) => {
      const bitmap = bitmapRef.current;
      if (!bitmap) return;
      setBusy(true);

      // Paint into a fresh canvas rather than reusing the on-screen one, so the
      // in-progress outline can never be baked into the exported image.
      const out = document.createElement('canvas');
      out.width = bitmap.width;
      out.height = bitmap.height;
      const context = out.getContext('2d', { willReadFrequently: true });
      if (!context) {
        setBusy(false);
        return;
      }

      // Count the arrows before the boxes go on, then again after, so the cost
      // of this redaction is a number rather than a caution.
      context.drawImage(bitmap, 0, 0);
      const before = detectArrows(
        context.getImageData(0, 0, out.width, out.height).data,
        out.width,
        out.height,
      ).blobs.length;

      context.fillStyle = '#000';
      for (const box of boxes) context.fillRect(box.x, box.y, box.w, box.h);
      const after = detectArrows(
        context.getImageData(0, 0, out.width, out.height).data,
        out.width,
        out.height,
      ).blobs.length;

      if (after < before && !acknowledged) {
        setArrowCost({ before, after });
        setBusy(false);
        return;
      }

      out.toBlob(
        (blob) => {
          setBusy(false);
          if (blob) onApply(blob);
        },
        'image/webp',
        0.92,
      );
    },
    [boxes, onApply],
  );

  return (
    <div className="panel">
      <div className="panel__inner">
        <p className="case-eyebrow">Step 1 of 2</p>
        <h1>Clean the screenshot</h1>

        <p className="lede">
          Drag over anything that should not be in the game. The boxes are
          painted into the image before it is uploaded — the original never
          leaves this browser.
        </p>

        <h2>Look for</h2>
        <ul className="rules">
          <li>
            <b>Real people.</b> Names, avatars, email addresses, @mentions and
            profile photos in the corner of a card.
          </li>
          <li>
            <b>Customers.</b> Company names, project keys, ticket text, anything
            in a URL bar or a browser tab title.
          </li>
          <li>
            <b>Anything that identifies your instance.</b> Site hostnames,
            licence keys, org ids, session tokens in a URL.
          </li>
          <li>
            <b>The red arrows must stay visible.</b> A box over an arrow removes
            it from the level — the game measures the arrows after this step.
          </li>
        </ul>

        {size ? (
          <>
            <div className="redactor">
              <canvas
                ref={canvasRef}
                width={size.w}
                height={size.h}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
              />
            </div>
            <p className="fineprint">
              {size.w}×{size.h} · {boxes.length} area
              {boxes.length === 1 ? '' : 's'} redacted
            </p>
            <div className="reset__actions" style={{ marginBottom: 18 }}>
              <button
                type="button"
                className="btn btn--ghost"
                disabled={boxes.length === 0}
                onClick={() => {
                  setArrowCost(null);
                  setBoxes((previous) => previous.slice(0, -1));
                }}
              >
                Undo last box
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                disabled={boxes.length === 0}
                onClick={() => {
                  setArrowCost(null);
                  setBoxes([]);
                }}
              >
                Clear all
              </button>
            </div>
          </>
        ) : (
          <p className="fineprint">Loading the image…</p>
        )}

        {/*
          Not a substitute for looking, and it does not verify anything. It is
          here to interrupt the reflex of clicking straight through, because
          nothing downstream can catch what this step misses.
        */}
        <label className="consent">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
          />
          <span>
            I have looked over this image and there is no real person's name,
            no customer information, and nothing that identifies a live system.
          </span>
        </label>

        {arrowCost && (
          <div className="form__message" role="alert">
            <b>
              These boxes cover {arrowCost.before - arrowCost.after} of{' '}
              {arrowCost.before} arrows.
            </b>{' '}
            A covered arrow is not measured, so it will not exist in the level —
            the puzzle will have {arrowCost.after} arrows, not {arrowCost.before}
            . If that is not what you meant, undo the box and re-crop the
            screenshot instead.
          </div>
        )}

        <div className="sheet__actions" style={{ marginTop: 18 }}>
          <button
            type="button"
            className="btn"
            disabled={!confirmed || !size || busy}
            onClick={() => apply(Boolean(arrowCost))}
          >
            {busy
              ? 'Cleaning…'
              : arrowCost
                ? `Continue with ${arrowCost.after} arrows`
                : 'Apply and measure the arrows'}
          </button>
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
